import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Check, Download, ExternalLink, FileText, HelpCircle, X } from "lucide-mui";
import { useState, type ReactElement } from "react";
import Markdown from "react-markdown";
import { Link as RouterLink } from "react-router-dom";
import type {
  Attachment,
  SignalBrief,
  SignalBriefAssessmentAnswer,
  SignalBriefSource,
} from "../../api/client.ts";
import { useTenant } from "../../contexts/TenantContext.tsx";
import ResearchVerdictChip, { ASSESSMENT_GATES } from "./ResearchVerdictChip.tsx";
import { formatBytes, formatDate, humanize } from "../../utils/format.ts";
import CountryName from "../CountryName.tsx";
import { FoldList, FoldRow, useOpenFolds } from "../FoldList.tsx";
import { RESEARCH_CONFIDENCE_SCALE } from "./VerdictCondition.tsx";
import StatusChip from "../StatusChip.tsx";

function AttachmentRow({ file }: { file: Attachment }) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start", py: 0.75 }}>
      <FileText fontSize="small" style={{ marginTop: 2, opacity: 0.6 }} />
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {file.title || file.filename}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {file.filename} · {formatBytes(file.size_bytes)}
          {file.source_url && (
            <>
              {" · "}
              <Link href={file.source_url} target="_blank" rel="noopener noreferrer">
                source
              </Link>
            </>
          )}
        </Typography>
        {file.description && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {file.description}
          </Typography>
        )}
      </Box>
      {file.download_url && (
        <Tooltip title="Download">
          <IconButton
            size="small"
            component="a"
            href={file.download_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Download fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

function todayIso(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** The buyer's name, its part in the event, and whether it is in the database. */
function BuyerLine({ brief }: { brief: SignalBrief }) {
  const { tenantPath } = useTenant();
  const details = brief.buyer_details ?? {};
  const notes = [details.role, details.country, details.org_number].filter(Boolean).join(" · ");
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
      <Typography variant="body2">
        Buyer:{" "}
        {brief.buyer_master_company_id ? (
          <Link component={RouterLink} to={tenantPath(`/companies/${brief.buyer_master_company_id}`)}>
            {brief.buyer_name}
          </Link>
        ) : (
          brief.buyer_name
        )}
        {notes && (
          <Typography component="span" variant="body2" color="text.secondary">
            {` · ${notes}`}
          </Typography>
        )}
      </Typography>
      {details.certainty === "presumed" && (
        <Tooltip title="Research inferred the buyer; no source names it">
          <Chip size="small" variant="outlined" label="Presumed" />
        </Tooltip>
      )}
      {!brief.buyer_master_company_id && (
        <Tooltip title="Not matched to a company in the database, so it is in no collection">
          <Chip size="small" variant="outlined" color="warning" label="Not in database" />
        </Tooltip>
      )}
    </Stack>
  );
}

/** The brief's structured fields: what to act on, for whom, and how carefully. */
export function AccountPanel({ brief }: { brief: SignalBrief }) {
  const [showCallBrief, setShowCallBrief] = useState(false);
  const holding = brief.hold_until !== null && brief.hold_until > todayIso();
  const hasAny =
    brief.country ||
    brief.trigger_type ||
    brief.event_date ||
    brief.trigger_date ||
    brief.buyer_name ||
    brief.sensitivity ||
    brief.hold_until ||
    brief.recommended_roles.length > 0 ||
    brief.call_brief;
  if (!hasAny) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1}>
        {(brief.country || brief.trigger_type || brief.event_date || brief.trigger_date) && (
          <Typography variant="body2">
            {brief.trigger_type && <strong>{brief.trigger_type}</strong>}
            {brief.country && (
              <>
                {brief.trigger_type && " · "}
                <CountryName code={brief.country} />
              </>
            )}
            {brief.trigger_date && ` · published ${formatDate(brief.trigger_date)}`}
            {brief.event_date && ` · event ${formatDate(brief.event_date)}`}
          </Typography>
        )}
        {brief.buyer_name && <BuyerLine brief={brief} />}
        {(brief.sensitivity || brief.hold_until) && (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            {brief.sensitivity && (
              <StatusChip
                kind="signal_sensitivity"
                value={brief.sensitivity}
                label={`Sensitivity: ${brief.sensitivity}`}
              />
            )}
            {brief.hold_until && (
              <Typography
                variant="caption"
                sx={{ color: holding ? "error.main" : "text.secondary" }}
              >
                {holding ? "Hold — no contact before " : "Hold until "}
                {formatDate(brief.hold_until)}
              </Typography>
            )}
          </Stack>
        )}
        {brief.recommended_roles.length > 0 && (
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", mr: 0.5 }}>
              Approach:
            </Typography>
            {brief.recommended_roles.map((role, i) => (
              <Chip key={`${role}-${i}`} size="small" variant="outlined" label={role} />
            ))}
          </Stack>
        )}
        {brief.call_brief && (
          <Box>
            <Button size="small" onClick={() => setShowCallBrief(!showCallBrief)}>
              {showCallBrief ? "Hide call brief" : "Call brief"}
            </Button>
            <Collapse in={showCallBrief} unmountOnExit>
              <Box sx={{ "& p": { mt: 0, mb: 1 }, "& ul": { mt: 0 }, typography: "body2" }}>
                <Markdown>{brief.call_brief}</Markdown>
              </Box>
            </Collapse>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

/** Why the signal was selected, the facts the agent established and the outcome it drew from them. */
export function ExecutiveSummary({ brief }: { brief: SignalBrief }) {
  const summary = brief.executive_summary;
  if (!summary) return null;
  return (
    <Box>
      {summary.why_selected && (
        <Box
          sx={{
            mb: 1,
            py: 0.75,
            px: 1.25,
            borderLeft: 3,
            borderColor: "primary.main",
            bgcolor: "action.hover",
            borderRadius: 0.5,
          }}
        >
          <Typography variant="overline" color="primary" sx={{ lineHeight: 1.5, display: "block" }}>
            Why selected
          </Typography>
          <Typography variant="body2">{summary.why_selected}</Typography>
        </Box>
      )}
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {summary.outcome}
      </Typography>
      <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5, typography: "body2" }}>
        {summary.facts.map((fact, i) => (
          <li key={i}>{fact}</li>
        ))}
      </Box>
    </Box>
  );
}

const ANSWER_STYLE: Record<
  SignalBriefAssessmentAnswer["answer"],
  { color: "success" | "error" | "default"; icon: ReactElement }
> = {
  yes: { color: "success", icon: <Check /> },
  no: { color: "error", icon: <X /> },
  unclear: { color: "default", icon: <HelpCircle /> },
};

/** The questions the verdict follows from; hover a chip for the question and the agent's reason. */
export function AssessmentRow({ brief }: { brief: SignalBrief }) {
  if (!brief.assessment) return null;
  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
      {Object.entries(brief.assessment).map(([key, item]) => {
        const style = ANSWER_STYLE[item.answer];
        const gate = ASSESSMENT_GATES.find((g) => g.key === key);
        return (
          <Tooltip
            key={key}
            title={
              <>
                {gate && (
                  <Typography variant="caption" component="div" sx={{ fontWeight: 600 }}>
                    {gate.question}
                  </Typography>
                )}
                <Typography variant="caption" component="div">
                  {item.answer}: {item.reason}
                </Typography>
              </>
            }
          >
            <Chip
              size="small"
              variant="outlined"
              color={style.color}
              icon={style.icon}
              label={gate?.label ?? key}
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
}

/** Who the sources are from, once each: the publisher, else the site. */
function sourcesGist(sources: SignalBriefSource[]): string {
  const names = sources.map((s) => {
    if (s.publisher) return s.publisher;
    try {
      return new URL(s.url).hostname.replace(/^www\./, "");
    } catch {
      return s.title || s.url;
    }
  });
  return [...new Set(names)].join(", ");
}

/** A research brief, in full: verdict, headline, summary, account, sources, files. */
export default function SignalBriefPanel({
  brief,
  duplicateLink,
}: {
  brief: SignalBrief;
  // Where a duplicate's original signal opens, given its id.
  duplicateLink?: (signalId: string) => string;
}) {
  const { tenantPath } = useTenant();
  // People and sources fold away: detail, not for scanning. Folded again on the next brief.
  const folds = useOpenFolds(brief.id);
  const originalLink =
    duplicateLink ?? ((id: string) => tenantPath(`/workflows/signals?signal=${id}`));
  return (
    <Stack spacing={2}>
      <Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5 }}>
          <ResearchVerdictChip verdict={brief.verdict} confidence={brief.confidence} />
          {brief.confidence && (
            <Tooltip title={RESEARCH_CONFIDENCE_SCALE}>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Research confidence {brief.confidence}/5
              </Typography>
            </Tooltip>
          )}
        </Stack>
        <Typography variant="h6">{brief.headline}</Typography>
        {brief.duplicate_of_signal_id && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Not researched again: see the{" "}
            <Link component={RouterLink} to={originalLink(brief.duplicate_of_signal_id)}>
              original signal
            </Link>
            .
          </Typography>
        )}
      </Box>

      <ExecutiveSummary brief={brief} />
      <AssessmentRow brief={brief} />

      <AccountPanel brief={brief} />

      <Box sx={{ "& p": { mt: 0, mb: 1 }, "& ul": { mt: 0 }, typography: "body2" }}>
        <Markdown>{brief.summary}</Markdown>
      </Box>

      {brief.related_companies.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Companies involved
          </Typography>
          <Stack spacing={0.75}>
            {brief.related_companies.map((c, i) => (
              <Box key={`${c.name}-${i}`}>
                <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                  {c.master_company_id ? (
                    <Link component={RouterLink} to={tenantPath(`/companies/${c.master_company_id}`)}>
                      {c.name}
                    </Link>
                  ) : (
                    <Typography variant="body2">{c.name}</Typography>
                  )}
                  {c.role && (
                    <Chip size="small" variant="outlined" label={humanize(c.role)} />
                  )}
                </Stack>
                {c.reason && (
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {c.reason}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {brief.attachments.length > 0 && (
        <Box>
          <Typography variant="subtitle2">Attachments</Typography>
          {brief.attachments.map((file) => (
            <AttachmentRow key={file.id} file={file} />
          ))}
        </Box>
      )}

      {(brief.people.length > 0 || brief.sources.length > 0) && (
        <FoldList>
          {brief.people.length > 0 && (
            <FoldRow
              label="People named"
              summary={`${brief.people.length} · ${brief.people.map((p) => p.name).join(", ")}`}
              expanded={folds.isOpen("people")}
              onToggle={() => folds.toggle("people")}
            >
              <Stack spacing={0.75}>
                {brief.people.map((p, i) => (
                  <Box key={`${p.name}-${i}`}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: "baseline" }}>
                      <Typography variant="body2">{p.name}</Typography>
                      {(p.title || p.organisation) && (
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {p.title}
                          {p.title && p.organisation && " · "}
                          {p.organisation &&
                            (p.master_company_id ? (
                              <Link component={RouterLink} to={tenantPath(`/companies/${p.master_company_id}`)}>
                                {p.organisation}
                              </Link>
                            ) : (
                              p.organisation
                            ))}
                        </Typography>
                      )}
                      {p.source_url && (
                        <Link
                          href={p.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Source naming ${p.name}`}
                          sx={{ display: "inline-flex" }}
                        >
                          <ExternalLink fontSize="inherit" />
                        </Link>
                      )}
                    </Stack>
                    {p.involvement && (
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        {p.involvement}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </FoldRow>
          )}
          {brief.sources.length > 0 && (
            <FoldRow
              label="Sources"
              summary={`${brief.sources.length} · ${sourcesGist(brief.sources)}`}
              expanded={folds.isOpen("sources")}
              onToggle={() => folds.toggle("sources")}
            >
              <Stack spacing={0.75}>
                {brief.sources.map((s, i) => (
                  <Box key={`${s.url}-${i}`}>
                    <Link
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                    >
                      {s.title || s.url}
                      <ExternalLink fontSize="inherit" />
                    </Link>
                    {(s.publisher || s.published_at) && (
                      <Typography variant="caption" sx={{ color: "text.secondary", ml: 1 }}>
                        {[s.publisher, s.published_at].filter(Boolean).join(" · ")}
                      </Typography>
                    )}
                    {s.note && (
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {s.note}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </FoldRow>
          )}
        </FoldList>
      )}
    </Stack>
  );
}
